import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { NavLeaf } from './types';
import {
  applyExpandedState,
  collectDescendantIds,
  collapseNavToRouteAncestors,
  expandAllNavNodes,
  filterNavCommands,
  findAncestorIdsForPath,
  flattenNavItems,
  parseExpandedIds,
  readNavMenuState,
  removeIdsFromExpandedSet,
  writeNavMenuState
} from './navMenuState';

interface PlatformSideNavProps {
  items: NavLeaf[];
  navStateCookieName: string;
  onNavigate?: () => void;
  showMenuSearch?: boolean;
  showExpandAll?: boolean;
  showCollapseAll?: boolean;
}

export function PlatformSideNav({
  items: filteredItems,
  navStateCookieName,
  onNavigate,
  showMenuSearch = false,
  showExpandAll = false,
  showCollapseAll = false
}: PlatformSideNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const initialState = useMemo(() => readNavMenuState(navStateCookieName), [navStateCookieName]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => parseExpandedIds(initialState.expandedIds));
  const [searchOpen, setSearchOpen] = useState(
    () => showMenuSearch && initialState.searchOpen === true
  );
  const [searchQuery, setSearchQuery] = useState('');

  const ancestorIds = useMemo(
    () => findAncestorIdsForPath(filteredItems, location.pathname),
    [filteredItems, location.pathname]
  );

  const items = useMemo(
    () => applyExpandedState(filteredItems, expandedIds, ancestorIds),
    [filteredItems, expandedIds, ancestorIds]
  );

  const navCommands = useMemo(() => flattenNavItems(filteredItems), [filteredItems]);
  const searchResults = useMemo(
    () => filterNavCommands(navCommands, searchQuery),
    [navCommands, searchQuery]
  );

  const focusSearchInput = useCallback(() => {
    window.setTimeout(() => {
      searchContainerRef.current?.querySelector('input')?.focus();
    }, 0);
  }, []);

  const persistState = useCallback(
    (nextExpanded: Set<string>, nextSearchOpen: boolean) => {
      writeNavMenuState(navStateCookieName, { expandedIds: [...nextExpanded], searchOpen: nextSearchOpen });
    },
    [navStateCookieName]
  );

  const setSearchOpenState = useCallback(
    (open: boolean) => {
      setSearchOpen(open);
      persistState(expandedIds, open);
      if (!open) setSearchQuery('');
    },
    [expandedIds, persistState]
  );

  useEffect(() => {
    if (!showMenuSearch && searchOpen) {
      setSearchOpenState(false);
    }
  }, [searchOpen, setSearchOpenState, showMenuSearch]);

  useEffect(() => {
    if (!showMenuSearch) return;

    function onKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const typingInField =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;

      if (e.key === 'Escape' && searchOpen) {
        e.preventDefault();
        setSearchOpenState(false);
        return;
      }

      if (typingInField) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpenState(true);
        focusSearchInput();
        return;
      }

      if (e.key === '/' && !searchOpen) {
        e.preventDefault();
        setSearchOpenState(true);
        focusSearchInput();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [searchOpen, setSearchOpenState, focusSearchInput, showMenuSearch]);

  useEffect(() => {
    if (showMenuSearch && searchOpen) focusSearchInput();
  }, [searchOpen, focusSearchInput, showMenuSearch]);

  function handleExpandedChange(next: Set<string>) {
    setExpandedIds(next);
    persistState(next, searchOpen);
  }

  function expandItem(item: NavLeaf) {
    if (!item.id) return;
    const next = new Set<string>(expandedIds);
    next.add(item.id);
    handleExpandedChange(next);
  }

  function collapseItem(item: NavLeaf) {
    if (!item.id) return;
    const toRemove = [item.id, ...collectDescendantIds(item)];
    handleExpandedChange(removeIdsFromExpandedSet(expandedIds, toRemove));
  }

  function activateLeaf(item: { path?: string; onSelect?: () => void }) {
    if (item.onSelect) {
      item.onSelect();
      onNavigate?.();
      return;
    }
    if (!item.path) return;
    navigate(item.path);
    onNavigate?.();
  }

  function navigateToCommand(item: { path: string; onSelect?: () => void }) {
    activateLeaf(item);
    setSearchQuery('');
    setSearchOpenState(false);
  }

  function handleExpandAll() {
    handleExpandedChange(expandAllNavNodes(filteredItems));
  }

  function handleCollapseAll() {
    handleExpandedChange(collapseNavToRouteAncestors(ancestorIds));
  }

  function navigateToItem(item: NavLeaf) {
    if (item.disabled) return;
    activateLeaf(item);
  }

  function toggleBranch(item: NavLeaf) {
    const isExpanded = expandedIds.has(item.id) || ancestorIds.has(item.id);
    if (isExpanded) {
      collapseItem(item);
    } else {
      expandItem(item);
    }
  }

  function isActiveItem(item: NavLeaf): boolean {
    if (!item.path) return false;
    const current = location.pathname.length > 1 && location.pathname.endsWith('/')
      ? location.pathname.slice(0, -1)
      : location.pathname;
    const target = item.path.length > 1 && item.path.endsWith('/') ? item.path.slice(0, -1) : item.path;
    return current === target;
  }

  function renderNavItems(nodes: NavLeaf[], depth = 0) {
    return (
      <div className={depth === 0 ? 'side-nav-list' : 'side-nav-sublist'} role={depth === 0 ? 'tree' : 'group'}>
        {nodes.map((item) => {
          const hasChildren = Boolean(item.items?.length);
          const isExpanded = hasChildren && (expandedIds.has(item.id) || ancestorIds.has(item.id));
          const active = isActiveItem(item);
          return (
            <div key={item.id} className="side-nav-node" role="none">
              <button
                type="button"
                className={[
                  'side-nav-row',
                  hasChildren ? 'side-nav-row--branch' : 'side-nav-row--leaf',
                  active ? 'side-nav-row--active' : '',
                  item.disabled ? 'side-nav-row--disabled' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="treeitem"
                aria-expanded={hasChildren ? isExpanded : undefined}
                aria-current={active ? 'page' : undefined}
                disabled={item.disabled}
                onClick={() => (hasChildren ? toggleBranch(item) : navigateToItem(item))}
              >
                <span className="side-nav-row__text">{item.text}</span>
                {hasChildren ? (
                  <span className="side-nav-row__chevron" aria-hidden>
                    {isExpanded ? '⌃' : '›'}
                  </span>
                ) : null}
              </button>
              {hasChildren && isExpanded && item.items ? renderNavItems(item.items, depth + 1) : null}
            </div>
          );
        })}
      </div>
    );
  }

  const showTreeActions = showExpandAll || showCollapseAll;

  return (
    <div className="side-nav-panel">
      {showTreeActions ? (
        <div className="side-nav-header">
          <div className="side-nav-tree-actions">
            {showExpandAll ? (
              <button
                type="button"
                className="side-nav-text-btn"
                title="Expand all menu sections"
                onClick={handleExpandAll}
              >
                <i className="dx-icon dx-icon-chevrondown" aria-hidden />
                Expand all
              </button>
            ) : null}
            {showCollapseAll ? (
              <button
                type="button"
                className="side-nav-text-btn"
                title="Collapse menu sections (keeps the current page open)"
                onClick={handleCollapseAll}
              >
                <i className="dx-icon dx-icon-chevronup" aria-hidden />
                Collapse all
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="side-nav-tree">{renderNavItems(items)}</div>

      {showMenuSearch ? (
      <div className="side-nav-footer">
        <button
          type="button"
          className="side-nav-text-btn side-nav-text-btn--block"
          onClick={() => setSearchOpenState(!searchOpen)}
        >
          <i className="dx-icon dx-icon-search" aria-hidden />
          {searchOpen ? 'Hide menu search' : 'Search menu'}
        </button>

        {searchOpen ? (
          <div className="side-nav-search" ref={searchContainerRef}>
            <input
              type="search"
              className="side-nav-search-input"
              value={searchQuery}
              placeholder="Search menu..."
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="side-nav-search-hint">Ctrl+K or / to open - Esc to close</div>

            {searchQuery.trim() ? (
              <div className="side-nav-search-results" role="listbox" aria-label="Menu search results">
                {searchResults.length === 0 ? (
                  <div className="side-nav-search-empty">No menu items found</div>
                ) : (
                  searchResults.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="side-nav-search-result"
                      role="option"
                      onClick={() => navigateToCommand(item)}
                    >
                      {item.breadcrumb ? (
                        <span className="side-nav-search-result-breadcrumb">{item.breadcrumb}</span>
                      ) : null}
                      <span className="side-nav-search-result-label">{item.text}</span>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      ) : null}
    </div>
  );
}
